<template>
    <Teleport
        v-if="isMounted && props.active"
        to="body"
    >
        <div :class="[$style.holder, props.isGameViewHidden && $style.with_hidden_frame]">
            <div :class="$style.scroller">
                <div :class="$style.scroller_inner">
                    <div :class="[$style.content, appStore.isNativeAppEnv && $style.native]">
                        <slot />
                    </div>
                </div>
            </div>
        </div>
        <div
            ref="large-viewport-probe"
            :class="$style.large_viewport_probe"
        />
        <div
            v-if="props.isTabsOpened"
            :class="$style.scroller_area"
            @click="update(false)"
        />
    </Teleport>
    <slot v-else />
</template>

<script setup lang="ts">
import { useMounted } from '@vueuse/core'
import { onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'

import { useBodyScrollLock } from '@/composables/use-body-scroll-lock'
import { useAppStore } from '@/store/app-store'

const appStore = useAppStore()

const props = defineProps<{
    active: boolean
    isGameViewHidden: boolean
    isTabsOpened: boolean
}>()

const emit = defineEmits<{
    'update:isTabsOpened': [value: boolean]
}>()

const isMounted = useMounted()
const largeViewportProbe = useTemplateRef('large-viewport-probe')

const requestAnimationFrameId = ref<number>()
const scrollLockTimeoutId = ref<number>()

const { setScrollLock } = useBodyScrollLock()

function update(value: boolean) {
    emit('update:isTabsOpened', value)
}

function measure() {
    if (typeof requestAnimationFrameId.value === 'number') {
        cancelAnimationFrame(requestAnimationFrameId.value)
    }

    if (props.isGameViewHidden) {
        setScrollLock(false)
        return
    }

    requestAnimationFrameId.value = requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--isl-vh', `${window.innerHeight * 0.01}px`)

        const largeViewportHeight = largeViewportProbe.value?.offsetHeight
        const open =
            !!window.visualViewport &&
            !!largeViewportHeight &&
            window.visualViewport.width > window.visualViewport.height &&
            Math.round(window.visualViewport.height + window.visualViewport.offsetTop) < largeViewportHeight

        if (open === props.isTabsOpened) {
            return
        }

        update(open)

        window.clearTimeout(scrollLockTimeoutId.value)
        scrollLockTimeoutId.value = window.setTimeout(
            () => {
                setScrollLock(!open)
            },
            open ? 0 : 100,
        )
    })
}

watch(
    () => props.isGameViewHidden,
    (hidden) => {
        if (!hidden && props.active) {
            measure()
        }
    },
)

onMounted(() => {
    if (!props.active) {
        return
    }
    measure()

    window.addEventListener('resize', measure, { passive: true })
    window.addEventListener('orientationchange', measure, { passive: true })
    window.addEventListener('scroll', measure, { passive: true })
    window.visualViewport?.addEventListener('resize', measure, { passive: true })
    window.visualViewport?.addEventListener('scroll', measure, { passive: true })
})

onBeforeUnmount(() => {
    if (!props.active) {
        return
    }

    window.removeEventListener('resize', measure)
    window.removeEventListener('orientationchange', measure)
    window.removeEventListener('scroll', measure)
    window.visualViewport?.removeEventListener('resize', measure)
    window.visualViewport?.removeEventListener('scroll', measure)

    if (requestAnimationFrameId.value) {
        cancelAnimationFrame(requestAnimationFrameId.value)
    }

    if (scrollLockTimeoutId.value) {
        clearTimeout(scrollLockTimeoutId.value)
    }
})
</script>

<style module>
.holder {
    position: fixed;
    inset: 0;
    width: 100%;
    height: min(calc(var(--isl-vh) * 100), 100%);
    overflow: hidden;
    z-index: 1;
    display: block;
}

.with_hidden_frame.holder {
    pointer-events: none;
    position: static;
}

.scroller {
    display: block;
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: none;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
}

.scroller_inner {
    display: flex;
    justify-content: flex-start;
    align-items: stretch;
    flex-direction: column;
    height: calc(100% + 1px);
    min-height: calc(100% + 1px);
}

.content {
    position: sticky;
    box-sizing: border-box;
    width: 100%;
    height: calc(100% - 1px);
    inset: 0;
    padding: var(--pg-safe-area-top) var(--pg-safe-area-right) 0 var(--pg-safe-area-left);
}

@media (--mobile-landscape) {
    .content {
        padding-right: 0;
    }

    .content.native {
        padding-left: 0;
    }
}

.large_viewport_probe {
    position: fixed;
    top: 0;
    left: 0;
    width: 0;
    height: 100vh;
    height: 100lvh;
    visibility: hidden;
    pointer-events: none;
}

.scroller_area {
    position: fixed;
    height: 400vh;
    inset: var(--mobile-top-bar-height) 0 0 0;
    z-index: 1;
}
</style>
